import { useEffect, useState } from "react";
import type { InstanceCalendarSourceList } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { calendarSetupStep, dismissCalendarSetup, isCalendarSetupDismissed } from "./calendarSetupOffer";

/**
 * KEY-815: "No calendars on this dial yet" — above a dial with a section of days until someone's calendar is on it.
 * Offered to whoever can add one (the calendar-sources read says so). If the viewer has already connected a calendar
 * it goes straight to the dial's Settings → Calendars; otherwise to Your settings to connect one first, which offers
 * the way back here once it's connected (KEY-769).
 */
export function CalendarSetupPrompt({ instanceId, onAdd, onConnect }: { instanceId: string; onAdd: () => void; onConnect: () => void }) {
  const [dismissed, setDismissed] = useState(() => isCalendarSetupDismissed(instanceId));
  const [list, setList] = useState<InstanceCalendarSourceList | null>(null);

  useEffect(() => {
    if (dismissed) return;
    const token = getAccessToken();
    if (!token) return;
    let live = true;
    api.instanceCalendarSources(token, instanceId)
      .then((next) => { if (live) setList(next); })
      // Best-effort: no list, no card. The dial's own Settings → Calendars still says what's wrong.
      .catch(() => {});
    return () => { live = false; };
  }, [instanceId, dismissed]);

  const step = calendarSetupStep(list);
  if (dismissed || step === "none") {
    return null;
  }

  return (
    <div
      className="kd-card"
      role="region"
      aria-label="Put a calendar on this dial"
      style={{ padding: "var(--pad-card, 16px)", margin: "0 0 14px", borderLeft: "3px solid var(--accent)" }}
    >
      <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--text)" }}>No calendars on this dial yet</div>
      <p style={{ margin: "6px 0 12px", fontSize: 13.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
        {step === "add"
          ? "Your calendar is connected. Put it on this dial and your appointments show up on the days they happen."
          : "This dial shows the next seven days from your calendar. Connect Google, Outlook or an iCal link in Your settings, then put it on this dial."}
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={step === "add" ? onAdd : onConnect} className="kd-btn-primary" style={{ fontSize: 13 }}>
          {step === "add" ? "Add your calendar" : "Connect your calendar"}
        </button>
        {/* kd-btn-dashed is the full-width "add" button; sized to its label here, as a secondary answer. */}
        <button
          onClick={() => {
            dismissCalendarSetup(instanceId);
            setDismissed(true);
          }}
          className="kd-btn-dashed"
          style={{ fontSize: 13, width: "auto", padding: "8px 16px" }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
